/**
 * Digital-mode camera orchestration.
 *
 * In digital mode (dreambooth.app) the consumer doesn't pick a camera via
 * the operator settings modal. Instead:
 *   - On mobile, we acquire the front camera by default (matches
 *     "creator mode" — the most common use case) using getUserMedia's
 *     `facingMode: 'user'` constraint.
 *   - On desktop, we use the browser's default camera selection (no
 *     facingMode hint), which usually picks the first available webcam.
 *   - The consumer can flip front↔back via the CameraSwitchButton on
 *     mobile. The choice persists to localStorage so a re-open keeps it.
 *
 * Aspect ratio: NO width/height/aspect-ratio constraints are passed — the
 * Webcam class uses only *ideal* hints. The stream returns the camera's
 * native dimensions; the preview UI fits to that. See
 * docs/digital-mode-plan.md §7.3.
 */

import { Webcam } from '@renderer/lib/webcam/webcam'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { isMobilePlatform } from '@renderer/lib/runtime/detector'

export type FacingMode = 'user' | 'environment'

const FACING_MODE_KEY = 'dreambooth.digitalCamera.facingMode'
const DEVICE_ID_KEY = 'dreambooth.digitalCamera.deviceId'

/** Read persisted desktop deviceId; empty string when none chosen yet. */
export function getDigitalDeviceId(): string {
  if (typeof window === 'undefined' || !window.localStorage) return ''
  return window.localStorage.getItem(DEVICE_ID_KEY) || ''
}

export function setDigitalDeviceId(deviceId: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, deviceId)
  } catch {
    // Quota exceeded / private mode — non-fatal; the choice just won't persist.
  }
}

/** Count connected video-input cameras — used to decide whether a desktop
 *  "switch camera" affordance is worth showing. */
export async function getDigitalCameraCount(): Promise<number> {
  try {
    const devices = await Webcam.getDevices()
    return devices.length
  } catch (err) {
    console.warn('[digitalCamera] enumerateDevices failed:', err)
    return 0
  }
}

/** Read persisted facingMode; defaults to 'user' (front camera). */
export function getDigitalFacingMode(): FacingMode {
  if (typeof window === 'undefined' || !window.localStorage) return 'user'
  const stored = window.localStorage.getItem(FACING_MODE_KEY)
  return stored === 'environment' ? 'environment' : 'user'
}

export function setDigitalFacingMode(mode: FacingMode): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(FACING_MODE_KEY, mode)
  } catch {
    // Quota exceeded / private mode — non-fatal; the choice just won't persist.
  }
}

/**
 * Connect the primary webcam for the digital consumer.
 *
 * Disconnects any existing primary webcam first so it's safe to call
 * repeatedly (e.g. after a permission re-prompt). Returns true on success.
 *
 * Mobile: applies the persisted (or default 'user') facingMode.
 * Desktop: no facingMode hint — browser picks the default.
 */
export async function connectDigitalCamera(): Promise<boolean> {
  const store = useCameraStore.getState()
  const existing = store.webcam
  if (existing) {
    try {
      await existing.disconnect()
    } catch (err) {
      console.warn('[digitalCamera] disconnect of existing webcam failed:', err)
    }
    store.setWebcam(null)
  }

  const mobile = isMobilePlatform()
  const facingMode = mobile ? getDigitalFacingMode() : undefined
  // Desktop: honor the consumer's cycled camera pick if there is one;
  // otherwise let the browser choose its default.
  const deviceId = mobile ? undefined : getDigitalDeviceId() || undefined
  const webcam = new Webcam()
  try {
    await webcam.connect(deviceId, facingMode)
  } catch (err) {
    // A persisted deviceId can go stale (camera unplugged / permissions reset),
    // producing an OverconstrainedError. Drop the pin and retry with the
    // browser default so the booth still comes up.
    if (deviceId) {
      console.warn('[digitalCamera] connect with saved deviceId failed, retrying default:', err)
      setDigitalDeviceId('')
      try {
        await webcam.connect(undefined, facingMode)
      } catch (retryErr) {
        console.error('[digitalCamera] webcam.connect failed:', retryErr)
        return false
      }
    } else {
      console.error('[digitalCamera] webcam.connect failed:', err)
      return false
    }
  }

  store.setWebcam(webcam)
  store.setDeviceType('webcam')
  return true
}

/**
 * Toggle the front/back camera on mobile and re-connect.
 *
 * On desktop this is a no-op — front/back facingMode is a mobile concept.
 * Desktop camera selection lives in switchDigitalCameraDesktop(). Returns the
 * resulting facingMode (unchanged if no-op).
 */
export async function switchDigitalCameraFacing(): Promise<FacingMode> {
  if (!isMobilePlatform()) {
    return getDigitalFacingMode()
  }
  const current = getDigitalFacingMode()
  const next: FacingMode = current === 'user' ? 'environment' : 'user'
  setDigitalFacingMode(next)
  await connectDigitalCamera()
  return next
}

/**
 * Cycle to the next available video-input device on desktop and re-connect.
 *
 * Desktops expose cameras by deviceId (not front/back facingMode), so this
 * mirrors what the operator SettingsModal dropdown offers — pick a different
 * camera — but as a one-tap "advance to the next one" for the simplified UI.
 * Enumerates the cameras, advances past the active one, persists the pick,
 * and re-acquires. No-op when fewer than two cameras are present.
 */
export async function switchDigitalCameraDesktop(): Promise<void> {
  const devices = await Webcam.getDevices()
  if (devices.length < 2) return

  // Determine the active deviceId: prefer the persisted pick, else fall back
  // to whatever the live stream reports (first switch had no stored id yet).
  let currentId = getDigitalDeviceId()
  if (!currentId) {
    try {
      const cfg = await useCameraStore.getState().webcam?.getConfig()
      currentId = cfg?.deviceId ?? ''
    } catch {
      currentId = ''
    }
  }

  const currentIdx = devices.findIndex((d) => d.deviceId === currentId)
  const next = devices[(currentIdx + 1) % devices.length]
  setDigitalDeviceId(next.deviceId)
  await connectDigitalCamera()
}

/**
 * Unified "switch camera" entry point for the digital consumer.
 * Mobile flips front/back; desktop cycles through connected cameras.
 */
export async function switchDigitalCamera(): Promise<void> {
  if (isMobilePlatform()) {
    await switchDigitalCameraFacing()
  } else {
    await switchDigitalCameraDesktop()
  }
}
